import { useState, useEffect, useRef } from 'react';
import { CircleIcon, FolderIcon, WarningCircleIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react';

import { Modal } from '@/ui/Modal';
import { Button, LinkButton } from '@/ui/button/Button';
import { SearchInput } from '@/ui/SearchInput';
import { Spinner } from '@/ui/Spinner';
import { LANGUAGE_COLORS } from '@/utils/language-colors';
import { settingsBase } from '@/features/settings/components/nav-config';
import { useSpace } from '@/contexts/space-context';

import type { ProviderRepo } from '@/api-models/provider-repos';

interface AddRepositoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  repos: ProviderRepo[];
  isLoadingRepos: boolean;
  onCreateRepository: (repo: ProviderRepo) => void;
  onSearch: (query?: string) => void;
  error?: { status: number; message: string } | null;
  onRetry?: () => void;
}

const DEBOUNCE_MS = 300;

export function AddRepositoryModal({
  isOpen,
  onClose,
  repos,
  isLoadingRepos,
  onCreateRepository,
  onSearch,
  error,
  onRetry,
}: AddRepositoryModalProps) {
  const { teamId } = useSpace();
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isFirstRender = useRef(true);

  // Debounce search → server (skip first render — parent handles initial load)
  useEffect(() => {
    if (!isOpen) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearch(search || undefined);
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      isFirstRender.current = true;
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New repository" description="Link a provider repository to add it">
      {/* Search */}
      <div className="px-6 pb-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search repositories..." />
      </div>

      {/* Repository list */}
      <div className="flex flex-col max-h-95 overflow-y-auto px-3 pb-4">
        {error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 px-6 text-center">
            <WarningCircleIcon size={28} weight="duotone" className="text-danger" />
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg">
                {error.status === 401 ? 'GitHub connection expired' : 'Failed to load repositories'}
              </span>
              <span className="text-xs text-fg-muted">
                {error.status === 401
                  ? 'Your GitHub credentials are no longer valid. Reconnect your account in Settings to continue.'
                  : error.message}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              {error.status === 401 && (
                <LinkButton variant="primary" size={7} to={`${settingsBase(teamId)}/integrations/source-control`}>
                  Go to Settings
                </LinkButton>
              )}
              {onRetry && (
                <Button variant="secondary" size={7} type="button" onClick={onRetry}>
                  <ArrowCounterClockwiseIcon size={12} />
                  Retry
                </Button>
              )}
            </div>
          </div>
        ) : isLoadingRepos ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={5} className="text-brand" />
          </div>
        ) : repos.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-xs text-fg-muted">
              {search ? 'No matching repositories' : 'No repositories found'}
            </span>
          </div>
        ) : (
          repos.map((repo) => {
            const repoLangColor = repo.language ? (LANGUAGE_COLORS[repo.language] ?? '#888') : null;
            return (
              <button
                key={repo.id}
                type="button"
                onClick={() => onCreateRepository(repo)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-accent cursor-pointer transition-colors duration-150"
              >
                <div className="flex size-8 items-center justify-center rounded-lg bg-surface-2 shrink-0">
                  <FolderIcon size={14} weight="duotone" className="text-fg-muted" />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                  <span className="text-xs font-medium text-fg truncate">{repo.full_name}</span>
                  {repo.description && <span className="text-2xs text-fg-muted truncate">{repo.description}</span>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {repoLangColor && repo.language && (
                    <div className="flex items-center gap-1.5">
                      <CircleIcon size={6} weight="fill" style={{ color: repoLangColor }} />
                      <span className="text-2xs text-fg-disabled">{repo.language}</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
}
